import {
    BasisActions,
    BasisState,
    CalendarActions,
    PackageActions,
    PackageState,
} from '@raskrask-dk/bookingflow-state-machine'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useBFSM } from '../../contexts/bfsmContext'

import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
    getIsServicePickerOpen,
    setIsServicePickerOpen,
} from '../../redux/reducers/calendarSettings'
import { useBFSMValue } from '../../services/hooks/useBFSMValues'
import { PackageCard } from '../../types/interfaceextension'
import {
    constructConcretepackages,
    getCheapestPackagesPerTemplateAndPackage,
} from './mappers'
import { KlippeKortTemplate } from './template'
import { useLockedBody } from '@raskrask-dk/rr-components'
import { BasisTypeBF, GoalBF, PartnerBF, ServiceBF } from '../../models'

export const KlippeKortPage = (): ReactElement => {
    const dispatch = useAppDispatch()
    const [, setLocked] = useLockedBody()

    const [BFSM] = useBFSM()
    const [basisType] = useBFSMValue<
        BasisState,
        BasisActions.SetBasisType,
        'basisType'
    >('basisType')
    const [goal] = useBFSMValue<PackageState, PackageActions.SetGoal, 'goal'>(
        'goal'
    )
    const goals = BFSM.service.schedules.goals as GoalBF[]
    const [service] = useBFSMValue<
        PackageState,
        PackageActions.SetService,
        'service'
    >('service')
    const services: ServiceBF[] = goal
        ? ((goal as GoalBF).services as ServiceBF[])
        : []
    const [partner, setPartner] = useBFSMValue<
        PackageState,
        PackageActions.SetPartner,
        'partner'
    >('partner', PackageActions.SetPartner)

    const [inspectedPartner, setInspectedPartner] = useState<
        PartnerBF | undefined
    >(undefined)

    // PackageTemplate from BFSM
    const [packageTemplate, setPackageTemplate] = useBFSMValue<
        PackageState,
        PackageActions.SetPackageTemplate,
        'packageTemplate'
    >('packageTemplate', PackageActions.SetPackageTemplate)

    const partners: PartnerBF[] = useMemo(() => {
        return service
            ? packageTemplate
                ? (packageTemplate.service as ServiceBF).getPartnersBy()
                : (service as ServiceBF).getPartnersBy()
            : []
    }, [service, packageTemplate])

    const packageTemplates = useMemo(() => {
        return partner && goal
            ? (partner as PartnerBF).getPackageTemplates(goal as GoalBF)
            : (goal as GoalBF).packages.filter((k) => {
                  return partners.some((p) => {
                      return p.servicetypes.some((s) => {
                          return s.id === k.service.id
                      })
                  })
              })
    }, [partners, goal, partner])

    // PackageCard from react
    const packageCards = useMemo(() => {
        const partnerThatiWantToCreatePackagesFor =
            partner === undefined ? partners : [partner]
        const concretePackagesOfPartners = constructConcretepackages(
            packageTemplates,
            partnerThatiWantToCreatePackagesFor
        )
        return getCheapestPackagesPerTemplateAndPackage(
            concretePackagesOfPartners
        )
    }, [packageTemplates, partners, partner])

    const selectedPackageCard = packageTemplate
        ? packageCards.filter((pc) => pc.template.id == packageTemplate.id)[0]
        : packageCards[0]

    //PackageTemplate
    const [inspectedPackage, setInspectedPackage] =
        useState<PackageCard>(selectedPackageCard)

    const goBack = () => {
        BFSM.goBack()
    }

    const goNext = () => {
        BFSM.doAction(PackageActions.Next, undefined)
    }

    const isCompanyBooking =
        BFSM.getData<BasisState>().customerType === 'Company'

    const isAggregated = partner === undefined ? true : false

    const servicePickerRef = useRef<HTMLDivElement>(null)

    const defaultModalType =
        partner !== undefined ? 'PartnerAndPackages' : 'Packages'

    const [modalType, setModalType] = useState<ModalType>(defaultModalType)

    const isServiceTypePickerOpen = useAppSelector((state) =>
        getIsServicePickerOpen(state)
    )

    // minHeight of servicePicker - for CSS purposes
    const [servicePickerMinHeight, setServicePickerMinHeight] = useState('0')

    const [isModalOpen, setIsModalOpen] = useState(false)

    const onServiceClick = (service: ServiceBF) => {
        goBack()
        BFSM.doAction(CalendarActions.SetServiceType, service)
    }
    const onGoalClick = (goal: GoalBF) => {
        goBack()
        BFSM.doAction(CalendarActions.SetGoal, goal)
    }

    const onClickKlippeCardButton = () => {
        return // TODO
    }

    const onClickGoToServicePicker = () => {
        dispatch(setIsServicePickerOpen(true))
    }

    const onClickPartner = (partner: PartnerBF) => {
        if (inspectedPartner?.id === partner.id) {
            setInspectedPartner(undefined)
        }
        setInspectedPartner(partner)
        setIsModalOpen(true)
    }

    useEffect(() => {
        if (servicePickerRef.current)
            setServicePickerMinHeight(
                isServiceTypePickerOpen
                    ? `calc(100vh - ${servicePickerRef.current.offsetTop}px - 32px)`
                    : '0'
            )
    }, [isServiceTypePickerOpen])

    useEffect(() => {
        setIsModalOpen(true)
    }, [])

    useEffect(() => {
        setLocked(isModalOpen)

        return () => setLocked(false)
    }, [isModalOpen, setLocked])

    return goal !== undefined ? (
        <KlippeKortTemplate
            basisType={basisType as BasisTypeBF}
            goalHandler={[goal as GoalBF, goals]}
            serviceHandler={[
                packageTemplate
                    ? (packageTemplate.service as ServiceBF)
                    : (service as ServiceBF),
                services,
            ]}
            partnerHandler={[
                partner as PartnerBF,
                setPartner,
                inspectedPartner,
                setInspectedPartner,
                partners,
            ]}
            packageHandler={[
                packageTemplate,
                setPackageTemplate,
                inspectedPackage,
                setInspectedPackage,
                packageCards,
                isAggregated,
            ]}
            goBack={goBack}
            goNext={goNext}
            isCompanyBooking={isCompanyBooking}
            isModalOpen={isModalOpen}
            modalType={modalType}
            setIsModalOpen={setIsModalOpen}
            setModalType={setModalType}
            servicePickerMinHeight={servicePickerMinHeight}
            servicePickerRef={servicePickerRef}
            isServiceTypePickerOpen={isServiceTypePickerOpen}
            onGoalClick={onGoalClick}
            onServiceClick={onServiceClick}
            onClickGoToServicePicker={onClickGoToServicePicker}
            onClickKlippeCardButton={onClickKlippeCardButton}
            onClickPartner={onClickPartner}
        />
    ) : (
        <></>
    )
}
